import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip,
  TableSortLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { Check as CheckIcon, Close as CloseIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { getAllOvertimes, confirmOvertime, unconfirmOvertime } from '../../services/overtimeAPIs';
import { updateOvertime, deleteOvertime } from '../../services/overtimeAPI';
import { getAllEmployees } from '../../services/EmployeeService';
import PageHeader from '../../components/headers/Header';

const OvertimeManagement = () => {
  const [overtimes, setOvertimes] = useState([]);
  const [employees, setEmployees] = useState({});
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('date');
  const [openModal, setOpenModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ date: '', hours: '', description: '', confirmed: '' });

  useEffect(() => {
    fetchOvertimes();
  }, []);

  const fetchOvertimes = async () => {
    try {
      setLoading(true);
      
      // Fetch employees and overtimes in parallel
      const [overtimesData, employeesData] = await Promise.all([
        getAllOvertimes(),
        getAllEmployees()
      ]);
      
      setOvertimes(overtimesData || []);
      
      // Create employee map for easy lookup
      if (employeesData && employeesData.length > 0) {
        const empMap = {};
        employeesData.forEach(emp => {
          const empId = emp.employeeId || emp.id;
          empMap[empId] = `${emp.firstName} ${emp.lastName}`;
        });
        setEmployees(empMap);
      }
      
    } catch (error) {
      console.error('Failed to fetch data:', error);
      showSnackbar('Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (err) {
      return dateString;
    }
  };

  const handleAction = async (id, confirmed) => {
    try {
      if (confirmed) {
        await confirmOvertime(id, true);
      } else {
        await unconfirmOvertime(id);
      }
      showSnackbar(`Overtime ${confirmed ? 'approved' : 'rejected'}`);
      fetchOvertimes();
    } catch (error) {
      showSnackbar('Failed to update overtime status', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this overtime request?')) {
      try {
        await deleteOvertime(id);
        showSnackbar('Overtime deleted');
        fetchOvertimes();
      } catch (error) {
        showSnackbar('Failed to delete overtime', 'error');
      }
    }
  };

  const handleOpenModal = (row) => {
    // Format date string to YYYY-MM-DD for the date input
    let formattedDate = '';
    if (row.date) {
      try {
        formattedDate = new Date(row.date).toISOString().split('T')[0];
      } catch (e) {
        formattedDate = row.date;
      }
    }
    
    setFormData({
      date: formattedDate,
      hours: row.hours || '',
      description: row.description || '',
      confirmed: row.confirmed === null ? 'pending' : row.confirmed.toString()
    });
    setEditingId(row.id);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingId(null);
    setFormData({ date: '', hours: '', description: '', confirmed: '' });
  };

  const handleSubmit = async () => {
    try {
      const confirmedValue = formData.confirmed === 'pending' ? null : formData.confirmed === 'true';
      const payload = {
        date: formData.date,
        hours: parseFloat(formData.hours),
        description: formData.description,
        confirmed: confirmedValue
      };
      
      await updateOvertime(editingId, payload);
      showSnackbar('Overtime updated successfully');
      handleCloseModal();
      fetchOvertimes();
    } catch (error) {
      console.error('Submit error:', error);
      showSnackbar('Failed to update overtime', 'error');
    }
  };

  const getStatusChip = (confirmed) => {
    if (confirmed === true) return <Chip label="Approved" color="success" size="small" />;
    if (confirmed === false) return <Chip label="Rejected" color="error" size="small" />;
    return <Chip label="Pending" color="warning" size="small" />;
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedOvertimes = [...overtimes].sort((a, b) => {
    let aVal = a[orderBy];
    let bVal = b[orderBy];
    
    if (orderBy === 'date') {
      const timeA = aVal ? new Date(aVal).getTime() : 0;
      const timeB = bVal ? new Date(bVal).getTime() : 0;
      aVal = isNaN(timeA) ? 0 : timeA;
      bVal = isNaN(timeB) ? 0 : timeB;
    } else if (orderBy === 'confirmed') {
      aVal = aVal === true ? 1 : aVal === false ? -1 : 0;
      bVal = bVal === true ? 1 : bVal === false ? -1 : 0;
    } else if (orderBy === 'employee') {
      const aName = a?.employee ? `${a.employee.firstName} ${a.employee.lastName}` : (employees[a.employeeId] || a.employeeId || '');
      const bName = b?.employee ? `${b.employee.firstName} ${b.employee.lastName}` : (employees[b.employeeId] || b.employeeId || '');
      aVal = aName;
      bName = bName;
    }
    
    if (aVal < bVal) return order === 'asc' ? -1 : 1;
    if (aVal > bVal) return order === 'asc' ? 1 : -1;
    return 0;
  });

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader title="Overtime Management" subtitle="Approve or reject employee overtime requests" />
      
      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead sx={{ backgroundColor: '#f8f9fa' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={orderBy === 'employee'}
                        direction={orderBy === 'employee' ? order : 'asc'}
                        onClick={() => handleRequestSort('employee')}
                      >
                        Employee
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={orderBy === 'date'}
                        direction={orderBy === 'date' ? order : 'asc'}
                        onClick={() => handleRequestSort('date')}
                      >
                        Date
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={orderBy === 'hours'}
                        direction={orderBy === 'hours' ? order : 'asc'}
                        onClick={() => handleRequestSort('hours')}
                      >
                        Hours
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={orderBy === 'confirmed'}
                        direction={orderBy === 'confirmed' ? order : 'asc'}
                        onClick={() => handleRequestSort('confirmed')}
                      >
                        Status
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedOvertimes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">No overtime requests found.</TableCell>
                    </TableRow>
                  ) : (
                    sortedOvertimes.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>
                          {row.employee ? (
                            `${row.employee.firstName} ${row.employee.lastName}`
                          ) : (
                            employees[row.employeeId] || row.employeeId || 'Unknown'
                          )}
                        </TableCell>
                        <TableCell>{formatDate(row.date)}</TableCell>
                        <TableCell>{row.hours}</TableCell>
                        <TableCell>{row.description}</TableCell>
                        <TableCell>{getStatusChip(row.confirmed)}</TableCell>
                        <TableCell align="right">
                          {/* Show actions only if it is pending */}
                            <>
                              {row.confirmed == null && (
                                <>
                                  <Tooltip title="Approve">
                                    <IconButton color="success" onClick={() => handleAction(row.id, true)}>
                                      <CheckIcon />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Reject">
                                    <IconButton color="error" onClick={() => handleAction(row.id, false)}>
                                      <CloseIcon />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                              <Tooltip title="Edit">
                                <IconButton color="primary" onClick={() => handleOpenModal(row)}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton color="error" onClick={() => handleDelete(row.id)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog 
        open={openModal} 
        onClose={handleCloseModal} 
        maxWidth="sm" 
        fullWidth
        disableRestoreFocus
        disableEnforceFocus
      >
        <DialogTitle>Edit Overtime Request</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            type="date"
            label="Date"
            InputLabelProps={{ shrink: true }}
            value={formData.date}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            type="number"
            inputProps={{ step: "0.5", min: "0.5" }}
            label="Hours"
            value={formData.hours}
            onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.confirmed}
              label="Status"
              onChange={(e) => setFormData({ ...formData, confirmed: e.target.value })}
            >
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="true">Approved</MenuItem>
              <MenuItem value="false">Rejected</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="secondary">Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">Update</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OvertimeManagement;
